// frontend/src/pages/crm/CreateWorkflow.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import { workflowAPI, formsAPI, emailTemplateAPI, usersAPI, leadAPI, opportunityAPI } from '../../utils/crmApi';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const CreateWorkflow = () => {
    const navigate = useNavigate();
    const { branding } = useBranding();
    const [loading, setLoading] = useState(false);
    const [forms, setForms] = useState([]);
    const [formsLoading, setFormsLoading] = useState(false);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [leads, setLeads] = useState([]);
    const [leadsLoading, setLeadsLoading] = useState(false);
    const [opportunities, setOpportunities] = useState([]);
    const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
    // Force refresh - updated data structure
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'task_automation',
        trigger: {
            type: 'event',
            event: 'client_created',
            conditions: [],
            eventFilters: {}
        },
        actions: [
            {
                type: 'send_email',
                order: 1,
                email: {
                    subject: '',
                    content: '',
                    recipients: []
                }
            }
        ],
        settings: {
            enabled: true,
            runOnce: false
        }
    });

    // Debug: Log initial form data
    console.log('Initial form data:', formData);

    const triggerTypes = [
        // Client events
        { value: 'client_created', label: 'Client Created', category: 'client' },
        { value: 'client_updated', label: 'Client Updated', category: 'client' },
        { value: 'client_status_changed', label: 'Client Status Changed', category: 'client' },
        // Task events
        { value: 'task_created', label: 'Task Created', category: 'task' },
        { value: 'task_completed', label: 'Task Completed', category: 'task' },
        { value: 'task_updated', label: 'Task Updated', category: 'task' },
        // Lead events
        { value: 'lead_created', label: 'Lead Created', category: 'lead' },
        { value: 'lead_updated', label: 'Lead Updated', category: 'lead' },
        { value: 'lead_qualified', label: 'Lead Qualified', category: 'lead' },
        { value: 'lead_converted', label: 'Lead Converted', category: 'lead' },
        // Email activity events
        { value: 'email_sent', label: 'Email Sent', category: 'email', needsFilter: true },
        { value: 'email_opened', label: 'Email Opened', category: 'email', needsFilter: true },
        { value: 'email_clicked', label: 'Email Clicked', category: 'email', needsFilter: true },
        { value: 'email_replied', label: 'Email Replied', category: 'email', needsFilter: true },
        { value: 'email_bounced', label: 'Email Bounced', category: 'email', needsFilter: true },
        // Form events
        { value: 'form_submitted', label: 'Form Submitted', category: 'form', needsFilter: true },
        { value: 'form_started', label: 'Form Started', category: 'form', needsFilter: true },
        { value: 'form_abandoned', label: 'Form Abandoned', category: 'form', needsFilter: true },
        // Website/URL events
        { value: 'url_visited', label: 'URL Visited', category: 'website', needsFilter: true },
        { value: 'link_clicked', label: 'Link Clicked', category: 'website', needsFilter: true },
        { value: 'resource_downloaded', label: 'Resource Downloaded', category: 'website', needsFilter: true },
        // Opportunity events
        { value: 'opportunity_created', label: 'Opportunity Created', category: 'opportunity' },
        { value: 'opportunity_updated', label: 'Opportunity Updated', category: 'opportunity' },
        { value: 'opportunity_stage_changed', label: 'Opportunity Stage Changed', category: 'opportunity' },
        // Status changes
        { value: 'status_changed', label: 'Status Changed', category: 'general' }
    ];

    const actionTypes = [
        { value: 'send_email', label: 'Send Email' },
        { value: 'create_task', label: 'Create Task' },
        { value: 'update_status', label: 'Update Status' },
        { value: 'assign_user', label: 'Assign User' },
        { value: 'send_notification', label: 'Send Notification' },
        { value: 'send_survey', label: 'Send Survey' },
        { value: 'webhook_call', label: 'Webhook Call' },
        { value: 'delay', label: 'Delay' },
        { value: 'condition_check', label: 'Condition Check' },
        // Lead actions
        { value: 'create_lead', label: 'Create Lead' },
        { value: 'update_lead', label: 'Update Lead' },
        { value: 'delete_lead', label: 'Delete Lead' },
        { value: 'convert_lead', label: 'Convert Lead to Client' },
        // Opportunity actions
        { value: 'create_opportunity', label: 'Create Opportunity' },
        { value: 'update_opportunity', label: 'Update Opportunity' },
        { value: 'delete_opportunity', label: 'Delete Opportunity' },
        // Entity updates
        { value: 'update_entity_params', label: 'Update Entity Parameters' }
    ];
    
    useEffect(() => {
        // Load published forms for survey action
        const loadForms = async () => {
            try {
                setFormsLoading(true);
                const { data } = await formsAPI.getAll({ status: 'published' });
                setForms(data?.data || []);
            } catch (error) {
                console.error('Failed to load forms:', error);
            } finally {
                setFormsLoading(false);
            }
        };
        
        // Load email templates for send_email action
        const loadEmailTemplates = async () => {
            try {
                setEmailTemplatesLoading(true);
                const { data } = await emailTemplateAPI.getAll();
                setEmailTemplates(data?.data || []);
            } catch (error) {
                console.error('Failed to load email templates:', error);
            } finally {
                setEmailTemplatesLoading(false);
            }
        };
        
        // Load users for assign_user action
        const loadUsers = async () => {
            try {
                setUsersLoading(true);
                const { data } = await usersAPI.getAll();
                setUsers(data?.data || []);
            } catch (error) {
                console.error('Failed to load users:', error);
            } finally {
                setUsersLoading(false);
            }
        };
        
        // Load leads for update_lead/delete_lead actions
        const loadLeads = async () => {
            try {
                setLeadsLoading(true);
                const { data } = await leadAPI.getAll({ limit: 100 });
                setLeads(data?.data || []);
            } catch (error) {
                console.error('Failed to load leads:', error);
            } finally {
                setLeadsLoading(false);
            }
        };
        
        // Load opportunities for update_opportunity/delete_opportunity actions
        const loadOpportunities = async () => {
            try {
                setOpportunitiesLoading(true);
                const { data } = await opportunityAPI.getAll({ limit: 100 });
                setOpportunities(data?.data || []);
            } catch (error) {
                console.error('Failed to load opportunities:', error);
            } finally {
                setOpportunitiesLoading(false);
            }
        };
        
        loadForms();
        loadEmailTemplates();
        loadUsers();
        loadLeads();
        loadOpportunities();
    }, []);

    const categories = [
        { value: 'task_automation', label: 'Task Automation' },
        { value: 'client_onboarding', label: 'Client Onboarding' },
        { value: 'lead_management', label: 'Lead Management' },
        { value: 'notification', label: 'Notification' },
        { value: 'reporting', label: 'Reporting' }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            console.log('Submitting workflow data:', formData);
            const response = await workflowAPI.create(formData);
            
            if (response.data?.success) {
                toast.success('Workflow created successfully!');
                navigate('/workflows');
            } else {
                toast.error(response.data?.message || 'Failed to create workflow');
            }
        } catch (error) {
            console.error('Error creating workflow:', error);
            toast.error(error.response?.data?.message || 'Failed to create workflow');
        } finally {
            setLoading(false);
        }
    };

    const addAction = () => {
        setFormData(prev => ({
            ...prev,
            actions: [
                ...prev.actions,
                {
                    type: 'send_email',
                    order: prev.actions.length + 1,
                    email: {
                        subject: '',
                        content: '',
                        recipients: []
                    }
                }
            ]
        }));
    };
    
    const updateActionSurvey = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            actions: prev.actions.map((action, i) => 
                i === index ? { 
                    ...action, 
                    survey: { ...action.survey, [field]: value }
                } : action
            )
        }));
    };

    const removeAction = (index) => {
        setFormData(prev => ({
            ...prev,
            actions: prev.actions.filter((_, i) => i !== index)
        }));
    };

    const updateAction = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            actions: prev.actions.map((action, i) => 
                i === index ? { ...action, [field]: value } : action
            )
        }));
    };

    const updateActionEmail = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            actions: prev.actions.map((action, i) => 
                i === index ? { 
                    ...action, 
                    email: { ...action.email, [field]: value }
                } : action
            )
        }));
    };

    const updateActionParams = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            actions: prev.actions.map((action, i) => 
                i === index ? { 
                    ...action, 
                    [field]: value
                } : action
            )
        }));
    };

    const updateActionNested = (index, section, field, value) => {
        setFormData(prev => ({
            ...prev,
            actions: prev.actions.map((action, i) => 
                i === index ? { 
                    ...action, 
                    [section]: { ...action[section], [field]: value }
                } : action
            )
        }));
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={() => navigate('/workflows')}
                    className="p-2 hover:bg-muted rounded-lg"
                >
                    <ArrowLeftIcon className="h-5 w-5" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Create Workflow</h1>
                    <p className="text-muted-foreground">Automate your business processes</p>
                    <div className="mt-2">
                        <button
                            onClick={() => navigate('/workflows/visual')}
                            className="text-sm hover:opacity-80 underline"
                            style={{ color: branding.primaryColor }}
                        >
                            Or use the Visual Workflow Builder →
                        </button>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="bg-card rounded-lg shadow-sm border p-6">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Basic Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                Workflow Name *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                style={{ '--tw-ring-color': branding.primaryColor }}
                                placeholder="e.g., New Client Onboarding"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                Category
                            </label>
                            <select
                                value={formData.category}
                                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                style={{ '--tw-ring-color': branding.primaryColor }}
                            >
                                {categories.map(cat => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            rows={3}
                                        className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                            placeholder="Describe what this workflow does..."
                        />
                    </div>
                </div>

                {/* Trigger */}
                <div className="bg-card rounded-lg shadow-sm border p-6">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Trigger</h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                When should this workflow run? *
                            </label>
                            <select
                                value={formData.trigger.event}
                                onChange={(e) => setFormData(prev => ({ 
                                    ...prev, 
                                    trigger: { 
                                        ...prev.trigger, 
                                        event: e.target.value,
                                        eventFilters: prev.trigger.eventFilters || {}
                                    }
                                }))}
                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                style={{ '--tw-ring-color': branding.primaryColor }}
                            >
                                <optgroup label="Client Events">
                                    {triggerTypes.filter(t => t.category === 'client').map(trigger => (
                                        <option key={trigger.value} value={trigger.value}>
                                            {trigger.label}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Task Events">
                                    {triggerTypes.filter(t => t.category === 'task').map(trigger => (
                                        <option key={trigger.value} value={trigger.value}>
                                            {trigger.label}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Lead Events">
                                    {triggerTypes.filter(t => t.category === 'lead').map(trigger => (
                                        <option key={trigger.value} value={trigger.value}>
                                            {trigger.label}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Email Activity">
                                    {triggerTypes.filter(t => t.category === 'email').map(trigger => (
                                        <option key={trigger.value} value={trigger.value}>
                                            {trigger.label}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Form Events">
                                    {triggerTypes.filter(t => t.category === 'form').map(trigger => (
                                        <option key={trigger.value} value={trigger.value}>
                                            {trigger.label}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Website/URL Events">
                                    {triggerTypes.filter(t => t.category === 'website').map(trigger => (
                                        <option key={trigger.value} value={trigger.value}>
                                            {trigger.label}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Opportunity Events">
                                    {triggerTypes.filter(t => t.category === 'opportunity').map(trigger => (
                                        <option key={trigger.value} value={trigger.value}>
                                            {trigger.label}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label="General">
                                    {triggerTypes.filter(t => t.category === 'general').map(trigger => (
                                        <option key={trigger.value} value={trigger.value}>
                                            {trigger.label}
                                        </option>
                                    ))}
                                </optgroup>
                            </select>
                        </div>
                        
                        {/* Email trigger filters */}
                        {formData.trigger.event && triggerTypes.find(t => t.value === formData.trigger.event)?.category === 'email' && (
                            <div className="space-y-4 p-4 bg-muted rounded-lg">
                                <h3 className="text-sm font-medium text-foreground">Email Filter</h3>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Email Template (optional)
                                    </label>
                                    <select
                                        value={formData.trigger.eventFilters?.emailTemplateId || ''}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            trigger: {
                                                ...prev.trigger,
                                                eventFilters: {
                                                    ...prev.trigger.eventFilters,
                                                    emailTemplateId: e.target.value || undefined
                                                }
                                            }
                                        }))}
                                        className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                    >
                                        <option value="">Any email template</option>
                                        {emailTemplatesLoading ? (
                                            <option disabled>Loading templates...</option>
                                        ) : emailTemplates.length === 0 ? (
                                            <option disabled>No email templates available</option>
                                        ) : (
                                            emailTemplates.map(template => (
                                                <option key={template._id} value={template._id}>
                                                    {template.name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Email Subject Contains (optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.trigger.eventFilters?.emailSubject || ''}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            trigger: {
                                                ...prev.trigger,
                                                eventFilters: {
                                                    ...prev.trigger.eventFilters,
                                                    emailSubject: e.target.value || undefined
                                                }
                                            }
                                        }))}
                                        className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                        placeholder="Filter by email subject"
                                    />
                                </div>
                            </div>
                        )}
                        
                        {/* Form trigger filters */}
                        {formData.trigger.event && triggerTypes.find(t => t.value === formData.trigger.event)?.category === 'form' && (
                            <div className="space-y-4 p-4 bg-muted rounded-lg">
                                <h3 className="text-sm font-medium text-foreground">Form Filter</h3>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Form (optional)
                                    </label>
                                    <select
                                        value={formData.trigger.eventFilters?.formId || ''}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            trigger: {
                                                ...prev.trigger,
                                                eventFilters: {
                                                    ...prev.trigger.eventFilters,
                                                    formId: e.target.value || undefined
                                                }
                                            }
                                        }))}
                                        className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                    >
                                        <option value="">Any form</option>
                                        {formsLoading ? (
                                            <option disabled>Loading forms...</option>
                                        ) : forms.length === 0 ? (
                                            <option disabled>No forms available</option>
                                        ) : (
                                            forms.map(form => (
                                                <option key={form._id} value={form._id}>
                                                    {form.name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                            </div>
                        )}
                        
                        {/* URL/Website trigger filters */}
                        {formData.trigger.event && triggerTypes.find(t => t.value === formData.trigger.event)?.category === 'website' && (
                            <div className="space-y-4 p-4 bg-muted rounded-lg">
                                <h3 className="text-sm font-medium text-foreground">URL Filter</h3>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        URL Pattern (optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.trigger.eventFilters?.urlPattern || ''}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            trigger: {
                                                ...prev.trigger,
                                                eventFilters: {
                                                    ...prev.trigger.eventFilters,
                                                    urlPattern: e.target.value || undefined
                                                }
                                            }
                                        }))}
                                        className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                        placeholder="e.g., /contact, /pricing, or regex pattern"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Leave empty to trigger on any URL
                                    </p>
                                </div>
                                {formData.trigger.event === 'link_clicked' && (
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Link Text Contains (optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.trigger.eventFilters?.linkText || ''}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                trigger: {
                                                    ...prev.trigger,
                                                    eventFilters: {
                                                        ...prev.trigger.eventFilters,
                                                        linkText: e.target.value || undefined
                                                    }
                                                }
                                            }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Filter by link text"
                                        />
                                    </div>
                                )}
                                {formData.trigger.event === 'resource_downloaded' && (
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Resource Type (optional)
                                        </label>
                                        <select
                                            value={formData.trigger.eventFilters?.resourceType || ''}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                trigger: {
                                                    ...prev.trigger,
                                                    eventFilters: {
                                                        ...prev.trigger.eventFilters,
                                                        resourceType: e.target.value || undefined
                                                    }
                                                }
                                            }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="">Any resource type</option>
                                            <option value="pdf">PDF</option>
                                            <option value="doc">Document</option>
                                            <option value="docx">Word Document</option>
                                            <option value="xls">Excel</option>
                                            <option value="image">Image</option>
                                            <option value="video">Video</option>
                                            <option value="zip">Archive</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="bg-card rounded-lg shadow-sm border p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-foreground">Actions</h2>
                        <button
                            type="button"
                            onClick={addAction}
                            className="flex items-center gap-2 px-3 py-2 text-white rounded-md hover:opacity-90"
                            style={{ backgroundColor: branding.primaryColor }}
                        >
                            <PlusIcon className="h-4 w-4" />
                            Add Action
                        </button>
                    </div>

                    {formData.actions.map((action, index) => (
                        <div key={index} className="border border-border rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-medium text-foreground">Action {index + 1}</h3>
                                {formData.actions.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeAction(index)}
                                        className="hover:opacity-80"
                                        style={{ color: branding.accentColor }}
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Action Type
                                    </label>
                                    <select
                                        value={action.type}
                                        onChange={(e) => {
                                            const newType = e.target.value;
                                            updateAction(index, 'type', newType);
                                            // Initialize action-specific fields when changing type
                                            if (newType === 'send_survey') {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    actions: prev.actions.map((a, i) => 
                                                        i === index ? { 
                                                            ...a, 
                                                            type: newType,
                                                            survey: {
                                                                formId: '',
                                                                recipientType: 'client',
                                                                message: ''
                                                            }
                                                        } : a
                                                    )
                                                }));
                                            }
                                        }}
                                        className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                style={{ '--tw-ring-color': branding.primaryColor }}
                                    >
                                        {actionTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {action.type === 'send_email' && (
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Email Template
                                        </label>
                                        <select
                                            value={action.email?.templateId || ''}
                                            onChange={(e) => {
                                                const templateId = e.target.value;
                                                const template = emailTemplates.find(t => t._id === templateId);
                                                updateActionEmail(index, 'templateId', templateId);
                                                if (template) {
                                                    updateActionEmail(index, 'subject', template.subject || '');
                                                    updateActionEmail(index, 'content', template.content || '');
                                                }
                                            }}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="">Select a template or enter manually...</option>
                                            {emailTemplatesLoading ? (
                                                <option disabled>Loading templates...</option>
                                            ) : emailTemplates.length === 0 ? (
                                                <option disabled>No email templates available</option>
                                            ) : (
                                                emailTemplates.map(template => (
                                                    <option key={template._id} value={template._id}>
                                                        {template.name}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                )}
                            </div>
                            
                            {/* Action-specific parameter fields */}
                            {action.type === 'send_email' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Email Subject
                                        </label>
                                        <input
                                            type="text"
                                            value={action.email?.subject || ''}
                                            onChange={(e) => updateActionEmail(index, 'subject', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Email subject"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Email Content
                                        </label>
                                        <textarea
                                            value={action.email?.content || ''}
                                            onChange={(e) => updateActionEmail(index, 'content', e.target.value)}
                                            rows={4}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Email content (HTML supported)"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Recipients
                                        </label>
                                        <select
                                            value={action.email?.recipientType || 'client'}
                                            onChange={(e) => updateActionEmail(index, 'recipientType', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="client">Client (from trigger)</option>
                                            <option value="lead">Lead (from trigger)</option>
                                            <option value="assigned_user">Assigned User</option>
                                            <option value="custom">Custom Email</option>
                                        </select>
                                    </div>
                                    {action.email?.recipientType === 'custom' && (
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Custom Email Address
                                            </label>
                                            <input
                                                type="email"
                                                value={action.email?.customEmail || ''}
                                                onChange={(e) => updateActionEmail(index, 'customEmail', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="recipient@example.com"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {action.type === 'create_task' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Task Title *
                                        </label>
                                        <input
                                            type="text"
                                            value={action.task?.title || ''}
                                            onChange={(e) => updateActionNested(index, 'task', 'title', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Task title"
                                            required
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Category
                                            </label>
                                            <select
                                                value={action.task?.category || 'other'}
                                                onChange={(e) => updateActionNested(index, 'task', 'category', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="visa">Visa</option>
                                                <option value="housing">Housing</option>
                                                <option value="banking">Banking</option>
                                                <option value="healthcare">Healthcare</option>
                                                <option value="education">Education</option>
                                                <option value="employment">Employment</option>
                                                <option value="legal">Legal</option>
                                                <option value="immigration">Immigration</option>
                                                <option value="relocation">Relocation</option>
                                                <option value="documentation">Documentation</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Priority
                                            </label>
                                            <select
                                                value={action.task?.priority || 'medium'}
                                                onChange={(e) => updateActionNested(index, 'task', 'priority', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="low">Low</option>
                                                <option value="medium">Medium</option>
                                                <option value="high">High</option>
                                                <option value="urgent">Urgent</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Description
                                        </label>
                                        <textarea
                                            value={action.task?.description || ''}
                                            onChange={(e) => updateActionNested(index, 'task', 'description', e.target.value)}
                                            rows={3}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Task description"
                                        />
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'update_status' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Entity Type
                                        </label>
                                        <select
                                            value={action.status?.entityType || 'client'}
                                            onChange={(e) => updateActionNested(index, 'status', 'entityType', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="client">Client</option>
                                            <option value="lead">Lead</option>
                                            <option value="task">Task</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            New Status *
                                        </label>
                                        <input
                                            type="text"
                                            value={action.status?.value || ''}
                                            onChange={(e) => updateActionNested(index, 'status', 'value', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="e.g., active, qualified, completed"
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'assign_user' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Entity Type *
                                        </label>
                                        <select
                                            value={action.assignment?.entity || 'client'}
                                            onChange={(e) => updateActionNested(index, 'assignment', 'entity', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="client">Client</option>
                                            <option value="lead">Lead</option>
                                            <option value="task">Task</option>
                                            <option value="account">Account</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Assign To *
                                        </label>
                                        <select
                                            value={action.assignment?.assignedTo || ''}
                                            onChange={(e) => updateActionNested(index, 'assignment', 'assignedTo', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        >
                                            <option value="">Select a user...</option>
                                            {usersLoading ? (
                                                <option disabled>Loading users...</option>
                                            ) : users.length === 0 ? (
                                                <option disabled>No users available</option>
                                            ) : (
                                                users.map(user => (
                                                    <option key={user._id} value={user._id}>
                                                        {user.name || user.email}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'delay' && (
                                <div className="mt-4 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Delay Amount *
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={action.delay?.amount || ''}
                                                onChange={(e) => updateActionNested(index, 'delay', 'amount', parseInt(e.target.value))}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Delay Unit *
                                            </label>
                                            <select
                                                value={action.delay?.unit || 'minutes'}
                                                onChange={(e) => updateActionNested(index, 'delay', 'unit', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="minutes">Minutes</option>
                                                <option value="hours">Hours</option>
                                                <option value="days">Days</option>
                                                <option value="weeks">Weeks</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'webhook_call' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Webhook URL *
                                        </label>
                                        <input
                                            type="url"
                                            value={action.webhook?.url || ''}
                                            onChange={(e) => updateActionNested(index, 'webhook', 'url', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="https://example.com/webhook"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            HTTP Method
                                        </label>
                                        <select
                                            value={action.webhook?.method || 'POST'}
                                            onChange={(e) => updateActionNested(index, 'webhook', 'method', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="POST">POST</option>
                                            <option value="GET">GET</option>
                                            <option value="PUT">PUT</option>
                                            <option value="PATCH">PATCH</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'send_notification' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Notification Title *
                                        </label>
                                        <input
                                            type="text"
                                            value={action.notification?.title || ''}
                                            onChange={(e) => updateActionNested(index, 'notification', 'title', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Notification title"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Notification Message *
                                        </label>
                                        <textarea
                                            value={action.notification?.message || ''}
                                            onChange={(e) => updateActionNested(index, 'notification', 'message', e.target.value)}
                                            rows={3}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Notification message"
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                            {action.type === 'send_survey' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Survey Form *
                                        </label>
                                        <select
                                            value={action.survey?.formId || ''}
                                            onChange={(e) => updateActionSurvey(index, 'formId', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        >
                                            <option value="">Select a form...</option>
                                            {formsLoading ? (
                                                <option disabled>Loading forms...</option>
                                            ) : forms.length === 0 ? (
                                                <option disabled>No published forms available</option>
                                            ) : (
                                                forms.map(form => (
                                                    <option key={form._id} value={form._id}>
                                                        {form.name}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Send To
                                        </label>
                                        <select
                                            value={action.survey?.recipientType || 'client'}
                                            onChange={(e) => updateActionSurvey(index, 'recipientType', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="client">Client (from trigger)</option>
                                            <option value="lead">Lead (from trigger)</option>
                                            <option value="email">Custom Email</option>
                                        </select>
                                    </div>
                                    {action.survey?.recipientType === 'email' && (
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                value={action.survey?.recipientEmail || ''}
                                                onChange={(e) => updateActionSurvey(index, 'recipientEmail', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="recipient@example.com"
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Custom Message (optional)
                                        </label>
                                        <textarea
                                            value={action.survey?.message || ''}
                                            onChange={(e) => updateActionSurvey(index, 'message', e.target.value)}
                                            rows={3}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Add a personalized message to include in the survey invite email..."
                                        />
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'create_lead' && (
                                <div className="mt-4 space-y-4">
                                    {/* Variable Helper */}
                                    <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                        <p className="text-xs font-semibold text-blue-900 dark:text-blue-200 mb-2">💡 Use Variables</p>
                                        <p className="text-xs text-blue-800 dark:text-blue-300 mb-2">
                                            You can use variables from form submissions or previous actions:
                                        </p>
                                        <div className="space-y-1 text-xs text-blue-700 dark:text-blue-400 font-mono">
                                            <div><code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">{"{{form.email}}"}</code> - Email from form</div>
                                            <div><code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">{"{{form.firstName}}"}</code> - First name from form</div>
                                            <div><code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">{"{{action1.email}}"}</code> - Email from action 1</div>
                                            <div><code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">{"{{answers.email}}"}</code> - Any form field</div>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                First Name
                                            </label>
                                            <input
                                                type="text"
                                                value={action.lead?.firstName || ''}
                                                onChange={(e) => updateActionNested(index, 'lead', 'firstName', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="John or {{form.firstName}}"
                                            />
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Auto-extracted from form if empty
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Last Name
                                            </label>
                                            <input
                                                type="text"
                                                value={action.lead?.lastName || ''}
                                                onChange={(e) => updateActionNested(index, 'lead', 'lastName', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Doe or {{form.lastName}}"
                                            />
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Auto-extracted from form if empty
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Email *
                                        </label>
                                        <input
                                            type="email"
                                            value={action.lead?.email || ''}
                                            onChange={(e) => updateActionNested(index, 'lead', 'email', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="email@example.com or {{form.email}}"
                                            required
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Auto-extracted from form if empty. Use {"{{form.email}}"} to reference form submission.
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Phone
                                            </label>
                                            <input
                                                type="tel"
                                                value={action.lead?.phone || ''}
                                                onChange={(e) => updateActionNested(index, 'lead', 'phone', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="+34 612 345 678 or {{form.phone}}"
                                            />
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Auto-extracted from form if empty
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Source
                                            </label>
                                            <select
                                                value={action.lead?.source || 'web_form'}
                                                onChange={(e) => updateActionNested(index, 'lead', 'source', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="web_form">Web Form</option>
                                                <option value="referral">Referral</option>
                                                <option value="social_media">Social Media</option>
                                                <option value="email_campaign">Email Campaign</option>
                                                <option value="phone_call">Phone Call</option>
                                                <option value="website">Website</option>
                                                <option value="search">Search</option>
                                                <option value="advertisement">Advertisement</option>
                                                <option value="event">Event</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Status
                                            </label>
                                            <select
                                                value={action.lead?.status || 'new'}
                                                onChange={(e) => updateActionNested(index, 'lead', 'status', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="new">New</option>
                                                <option value="contacted">Contacted</option>
                                                <option value="qualified">Qualified</option>
                                                <option value="nurturing">Nurturing</option>
                                                <option value="converted">Converted</option>
                                                <option value="disqualified">Disqualified</option>
                                                <option value="lost">Lost</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Assign To
                                            </label>
                                            <select
                                                value={action.lead?.assignedTo || ''}
                                                onChange={(e) => updateActionNested(index, 'lead', 'assignedTo', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="">Unassigned</option>
                                                {usersLoading ? (
                                                    <option disabled>Loading users...</option>
                                                ) : users.length === 0 ? (
                                                    <option disabled>No users available</option>
                                                ) : (
                                                    users.map(user => (
                                                        <option key={user._id} value={user._id}>
                                                            {user.name || user.email}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Company
                                        </label>
                                        <input
                                            type="text"
                                            value={action.lead?.company || ''}
                                            onChange={(e) => updateActionNested(index, 'lead', 'company', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Company name or {{form.company}}"
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Auto-extracted from form if empty
                                        </p>
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'update_lead' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Lead (optional - will find by email if not provided)
                                        </label>
                                        <select
                                            value={action.lead?.leadId || ''}
                                            onChange={(e) => updateActionNested(index, 'lead', 'leadId', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="">Auto-detect from form email</option>
                                            {leadsLoading ? (
                                                <option disabled>Loading leads...</option>
                                            ) : leads.length === 0 ? (
                                                <option disabled>No leads available</option>
                                            ) : (
                                                leads.map(lead => (
                                                    <option key={lead._id} value={lead._id}>
                                                        {lead.firstName} {lead.lastName} ({lead.email})
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            If not specified, will find lead by email from form submission
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Status
                                        </label>
                                        <select
                                            value={action.lead?.updates?.status || ''}
                                            onChange={(e) => {
                                                const updates = { ...action.lead?.updates, status: e.target.value || undefined };
                                                updateActionNested(index, 'lead', 'updates', updates);
                                            }}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="">No change</option>
                                            <option value="new">New</option>
                                            <option value="contacted">Contacted</option>
                                            <option value="qualified">Qualified</option>
                                            <option value="nurturing">Nurturing</option>
                                            <option value="converted">Converted</option>
                                            <option value="disqualified">Disqualified</option>
                                            <option value="lost">Lost</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Score Increment
                                        </label>
                                        <input
                                            type="number"
                                            min="-100"
                                            max="100"
                                            value={action.lead?.scoreIncrement || ''}
                                            onChange={(e) => updateActionNested(index, 'lead', 'scoreIncrement', e.target.value ? parseInt(e.target.value) : undefined)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="e.g., +5 or -2"
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Add or subtract from current lead score (will also update engagement)
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Assign To
                                        </label>
                                        <select
                                            value={action.lead?.updates?.assignedTo || ''}
                                            onChange={(e) => {
                                                const updates = { ...action.lead?.updates, assignedTo: e.target.value || undefined };
                                                updateActionNested(index, 'lead', 'updates', updates);
                                            }}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="">No change</option>
                                            {usersLoading ? (
                                                <option disabled>Loading users...</option>
                                            ) : users.length === 0 ? (
                                                <option disabled>No users available</option>
                                            ) : (
                                                users.map(user => (
                                                    <option key={user._id} value={user._id}>
                                                        {user.name || user.email}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'condition_check' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Condition Field *
                                        </label>
                                        <select
                                            value={action.condition?.field || ''}
                                            onChange={(e) => {
                                                const condition = { ...action.condition, field: e.target.value };
                                                updateActionNested(index, 'condition', 'field', e.target.value);
                                            }}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        >
                                            <option value="">Select condition...</option>
                                            <option value="lead_exists">Lead Exists (by email)</option>
                                            <option value="leadExists">Lead Exists (alternative)</option>
                                            <option value="form.email">Form Email</option>
                                            <option value="answers.email">Answers Email</option>
                                        </select>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Use "Lead Exists" to check if a lead with the form email already exists
                                        </p>
                                    </div>
                                    {action.condition?.field && (
                                        <>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Operator *
                                                </label>
                                                <select
                                                    value={action.condition?.operator || ''}
                                                    onChange={(e) => {
                                                        const condition = { ...action.condition, operator: e.target.value };
                                                        updateActionNested(index, 'condition', 'operator', e.target.value);
                                                    }}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    required
                                                >
                                                    <option value="">Select operator...</option>
                                                    {(action.condition?.field === 'lead_exists' || action.condition?.field === 'leadExists') ? (
                                                        <>
                                                            <option value="equals">Equals (true)</option>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <option value="equals">Equals</option>
                                                            <option value="not_equals">Not Equals</option>
                                                            <option value="contains">Contains</option>
                                                            <option value="not_contains">Not Contains</option>
                                                            <option value="greater_than">Greater Than</option>
                                                            <option value="less_than">Less Than</option>
                                                            <option value="is_empty">Is Empty</option>
                                                            <option value="is_not_empty">Is Not Empty</option>
                                                        </>
                                                    )}
                                                </select>
                                            </div>
                                            {action.condition?.field !== 'lead_exists' && action.condition?.field !== 'leadExists' && (
                                                <div>
                                                    <label className="block text-sm font-medium text-foreground mb-1">
                                                        Value
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={action.condition?.value || ''}
                                                        onChange={(e) => updateActionNested(index, 'condition', 'value', e.target.value)}
                                                        className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                                        placeholder="Value to compare or {{variable}}"
                                                    />
                                                </div>
                                            )}
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    If True - Execute Actions (action order numbers)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={action.condition?.trueActions?.join(', ') || ''}
                                                    onChange={(e) => {
                                                        const orders = e.target.value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
                                                        const condition = { ...action.condition, trueActions: orders };
                                                        updateActionNested(index, 'condition', 'trueActions', orders);
                                                    }}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="e.g., 2, 3, 4"
                                                />
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Comma-separated list of action order numbers to execute if condition is true
                                                </p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    If False - Execute Actions (action order numbers)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={action.condition?.falseActions?.join(', ') || ''}
                                                    onChange={(e) => {
                                                        const orders = e.target.value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
                                                        const condition = { ...action.condition, falseActions: orders };
                                                        updateActionNested(index, 'condition', 'falseActions', orders);
                                                    }}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="e.g., 5, 6"
                                                />
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Comma-separated list of action order numbers to execute if condition is false
                                                </p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            
                            {action.type === 'delete_lead' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Lead *
                                        </label>
                                        <select
                                            value={action.lead?.leadId || ''}
                                            onChange={(e) => updateActionNested(index, 'lead', 'leadId', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        >
                                            <option value="">Select a lead...</option>
                                            {leadsLoading ? (
                                                <option disabled>Loading leads...</option>
                                            ) : leads.length === 0 ? (
                                                <option disabled>No leads available</option>
                                            ) : (
                                                leads.map(lead => (
                                                    <option key={lead._id} value={lead._id}>
                                                        {lead.firstName} {lead.lastName} ({lead.email})
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                        <p className="text-sm text-yellow-800">
                                            ⚠️ This will permanently delete the selected lead. This action cannot be undone.
                                        </p>
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'convert_lead' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Lead *
                                        </label>
                                        <select
                                            value={action.lead?.leadId || ''}
                                            onChange={(e) => updateActionNested(index, 'lead', 'leadId', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        >
                                            <option value="">Select a lead...</option>
                                            {leadsLoading ? (
                                                <option disabled>Loading leads...</option>
                                            ) : leads.length === 0 ? (
                                                <option disabled>No leads available</option>
                                            ) : (
                                                leads.map(lead => (
                                                    <option key={lead._id} value={lead._id}>
                                                        {lead.firstName} {lead.lastName} ({lead.email})
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={action.lead?.convertToClient || false}
                                            onChange={(e) => updateActionNested(index, 'lead', 'convertToClient', e.target.checked)}
                                            className="h-4 w-4 border-input rounded focus:ring-2"
                                            style={{ 
                                                accentColor: branding.primaryColor,
                                                '--tw-ring-color': branding.primaryColor
                                            }}
                                        />
                                        <label className="ml-2 text-sm text-foreground">
                                            Convert to client automatically
                                        </label>
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'create_opportunity' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Opportunity Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={action.opportunity?.name || ''}
                                            onChange={(e) => updateActionNested(index, 'opportunity', 'name', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Opportunity name"
                                            required
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Value
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={action.opportunity?.value || ''}
                                                onChange={(e) => updateActionNested(index, 'opportunity', 'value', parseFloat(e.target.value))}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="0.00"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Stage
                                            </label>
                                            <select
                                                value={action.opportunity?.stage || 'prospecting'}
                                                onChange={(e) => updateActionNested(index, 'opportunity', 'stage', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="prospecting">Prospecting</option>
                                                <option value="qualification">Qualification</option>
                                                <option value="proposal">Proposal</option>
                                                <option value="negotiation">Negotiation</option>
                                                <option value="closed_won">Closed Won</option>
                                                <option value="closed_lost">Closed Lost</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Probability (%)
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={action.opportunity?.probability || ''}
                                                onChange={(e) => updateActionNested(index, 'opportunity', 'probability', parseInt(e.target.value))}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="10"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Type
                                            </label>
                                            <select
                                                value={action.opportunity?.type || 'new_business'}
                                                onChange={(e) => updateActionNested(index, 'opportunity', 'type', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="new_business">New Business</option>
                                                <option value="upsell">Upsell</option>
                                                <option value="cross_sell">Cross Sell</option>
                                                <option value="renewal">Renewal</option>
                                                <option value="relocation">Relocation</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Description
                                        </label>
                                        <textarea
                                            value={action.opportunity?.description || ''}
                                            onChange={(e) => updateActionNested(index, 'opportunity', 'description', e.target.value)}
                                            rows={3}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Opportunity description"
                                        />
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'update_opportunity' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Opportunity *
                                        </label>
                                        <select
                                            value={action.opportunity?.opportunityId || ''}
                                            onChange={(e) => updateActionNested(index, 'opportunity', 'opportunityId', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        >
                                            <option value="">Select an opportunity...</option>
                                            {opportunitiesLoading ? (
                                                <option disabled>Loading opportunities...</option>
                                            ) : opportunities.length === 0 ? (
                                                <option disabled>No opportunities available</option>
                                            ) : (
                                                opportunities.map(opp => (
                                                    <option key={opp._id} value={opp._id}>
                                                        {opp.name} ({opp.stage})
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Stage
                                            </label>
                                            <select
                                                value={action.opportunity?.updates?.stage || ''}
                                                onChange={(e) => {
                                                    const updates = { ...action.opportunity?.updates, stage: e.target.value || undefined };
                                                    updateActionNested(index, 'opportunity', 'updates', updates);
                                                }}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="">No change</option>
                                                <option value="prospecting">Prospecting</option>
                                                <option value="qualification">Qualification</option>
                                                <option value="proposal">Proposal</option>
                                                <option value="negotiation">Negotiation</option>
                                                <option value="closed_won">Closed Won</option>
                                                <option value="closed_lost">Closed Lost</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Probability (%)
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={action.opportunity?.updates?.probability || ''}
                                                onChange={(e) => {
                                                    const updates = { ...action.opportunity?.updates, probability: e.target.value ? parseInt(e.target.value) : undefined };
                                                    updateActionNested(index, 'opportunity', 'updates', updates);
                                                }}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="No change"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'delete_opportunity' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Opportunity *
                                        </label>
                                        <select
                                            value={action.opportunity?.opportunityId || ''}
                                            onChange={(e) => updateActionNested(index, 'opportunity', 'opportunityId', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        >
                                            <option value="">Select an opportunity...</option>
                                            {opportunitiesLoading ? (
                                                <option disabled>Loading opportunities...</option>
                                            ) : opportunities.length === 0 ? (
                                                <option disabled>No opportunities available</option>
                                            ) : (
                                                opportunities.map(opp => (
                                                    <option key={opp._id} value={opp._id}>
                                                        {opp.name} ({opp.stage})
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                        <p className="text-sm text-yellow-800">
                                            ⚠️ This will permanently delete the selected opportunity. This action cannot be undone.
                                        </p>
                                    </div>
                                </div>
                            )}
                            
                            {action.type === 'update_entity_params' && (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Entity Type *
                                        </label>
                                        <select
                                            value={action.entityUpdate?.entity || ''}
                                            onChange={(e) => updateActionNested(index, 'entityUpdate', 'entity', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        >
                                            <option value="">Select entity type...</option>
                                            <option value="lead">Lead</option>
                                            <option value="client">Client</option>
                                            <option value="account">Account</option>
                                            <option value="opportunity">Opportunity</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Entity ID *
                                        </label>
                                        <input
                                            type="text"
                                            value={action.entityUpdate?.entityId || ''}
                                            onChange={(e) => updateActionNested(index, 'entityUpdate', 'entityId', e.target.value)}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Entity ID (from trigger or enter manually)"
                                            required
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Use variables like {'{'}clientId{'}'} or {'{'}leadId{'}'} from trigger, or enter a specific ID
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Updates (JSON) *
                                        </label>
                                        <textarea
                                            value={typeof action.entityUpdate?.updates === 'object' ? JSON.stringify(action.entityUpdate.updates, null, 2) : action.entityUpdate?.updates || ''}
                                            onChange={(e) => {
                                                try {
                                                    const updates = JSON.parse(e.target.value);
                                                    updateActionNested(index, 'entityUpdate', 'updates', updates);
                                                } catch {
                                                    // Invalid JSON, store as string for now
                                                    updateActionNested(index, 'entityUpdate', 'updates', e.target.value);
                                                }
                                            }}
                                            rows={6}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring font-mono text-sm"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder='{"status": "active", "priority": "high"}'
                                            required
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Enter field updates as JSON object
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Settings */}
                <div className="bg-card rounded-lg shadow-sm border p-6">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Settings</h2>
                    <div className="space-y-4">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="enabled"
                                checked={formData.settings.enabled}
                                onChange={(e) => setFormData(prev => ({ 
                                    ...prev, 
                                    settings: { ...prev.settings, enabled: e.target.checked }
                                }))}
                                className="h-4 w-4 border-input rounded focus:ring-2 focus:border-transparent"
                                style={{ 
                                    color: branding.primaryColor,
                                    '--tw-ring-color': branding.primaryColor
                                }}
                            />
                            <label htmlFor="enabled" className="ml-2 text-sm text-foreground">
                                Enable this workflow
                            </label>
                        </div>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="runOnce"
                                checked={formData.settings.runOnce}
                                onChange={(e) => setFormData(prev => ({ 
                                    ...prev, 
                                    settings: { ...prev.settings, runOnce: e.target.checked }
                                }))}
                                className="h-4 w-4 border-input rounded focus:ring-2 focus:border-transparent"
                                style={{ 
                                    color: branding.primaryColor,
                                    '--tw-ring-color': branding.primaryColor
                                }}
                            />
                            <label htmlFor="runOnce" className="ml-2 text-sm text-foreground">
                                Run only once per trigger
                            </label>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-4">
                    <button
                        type="button"
                        onClick={() => navigate('/workflows')}
                        className="px-4 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md text-foreground hover:bg-muted"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50"
                        style={{ backgroundColor: branding.primaryColor }}
                    >
                        {loading ? 'Creating...' : 'Create Workflow'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateWorkflow;
